// 絵と音を読む順番 (11-02・docs/research/2026-11-02-load-order-rework.md)。
//   11-02 作者「1.お願いします」(問い＝1.5Mbps ほどの遅い回線の人にも、絵の欠けが出ないようにするか)。
//   起動すると 20 以上の部品が読み込みと同時に全ボス分の絵と音を頼み、約83MB・557本が回線を等分していた。
//   遅い回線では、いま見るプロローグの絵も先のボスの FX と同じ速さでしか届かなかった。
//   ここでは頼まれた物を捨てず、遅らせるだけにする (読めた後の見た目と遊びは前と同じ)。
//     段0 = いま見る会話場面・タイトル・UI・字形 → すぐ読む。
//     段1 = いまのボスの戦い (絵・部屋の背景・技の音)・主人公・どのボスでも使う戦いの物 → 段0 が読んでいる間は2本まで。
//     段2 = 次のボス・致命の FX (戦いの終わりにだけ要る) → 段0・段1 が空いた時に2本まで。
//     段3 = それより先のボス → 段2 と同じ枠で、本編の順に。
//   いまのボスが替わったら (setAssetFocus) 並んでいる物の段を付け直す。
//   11-03: 段3 (先のボス) は、そのボスが次かいまになるまで読み始めない (作者 memo「ボスごとに読む方が良いのでは」)。
//     起動時に全ボス分の FX が頼まれても、回線に流すのはいまと次のボスの分だけ。前の版 (段3 も空いたら読む) は開発版の ?bossFx=startup。
//   どのボスの物かは URL の言葉で見分ける (見分け違いは読む時刻が変わるだけで、絵が欠けたままにはならない)。

/** 本編で戦う順 (src/bosses.ts の BOSS_DEFINITIONS と同じ・tests/asset_queue.test.ts が見張る)。 */
export const ASSET_BOSS_ORDER = [
  'nameless-shade', 'dream-hound', 'vigil-knight', 'bride', 'the-executioner',
  'crowned-king-awoken', 'inverted-jester', 'hollow-actor', 'blade-dancer', 'former-princess',
] as const

// どのボスでも使う物 (名前にボスの言葉が入っていても持ち主なし)。段3 で番まで止めると前のボスで欠けるので、先に外す (11-03・起動時に頼む 356 本を見て拾った)。
//   /fx/exec/ = 処刑人の名の床の砂・ひび・霧・閃光 (src/exec_fx_mats.ts・exec_fx_sprites.ts)。床の予告 (floor_warn_vis)・地割れ (quake_fx)・道化・剣舞・双子も使う。
//   luna_exec_ / parry_exec_ = 主人公の致命の重み・致命を弾いた押し返し。
const SHARED_WORDS = /\/fx\/exec\/|luna_exec_|parry_exec_/
// URL (小文字) に含まれる言葉 → ボス。上から順に見る。
//   exec_unique_knight_ / exec_unique_bride_ は騎士の固有のとどめと弾かれた花嫁の音 (src/exec_unique_sound.ts) なので、exec より先に見る。
const OWNER_WORDS: [RegExp, string][] = [
  [/exec_unique_knight_/, 'vigil-knight'],
  [/exec_unique_bride_/, 'bride'],
  [/the-executioner|executioner|\/exec\/|\/exec_|exec_|cannon/, 'the-executioner'],
  [/crowned-king-awoken|awoken|rampage|grudge|floor_wave|wave_rift/, 'crowned-king-awoken'],
  [/nameless-shade|shade|prologue/, 'nameless-shade'],
  [/dream-hound|hound/, 'dream-hound'],
  [/vigil-knight|knight|lance/, 'vigil-knight'],
  [/bride/, 'bride'],
  [/inverted-jester|jester|rooster/, 'inverted-jester'],
  [/blade-dancer|blade/, 'blade-dancer'],
  [/hollow-actor|hollow|twins/, 'hollow-actor'],
  [/former-princess|princess|flashback/, 'former-princess'],
]

// 衣装を借りて戦うボス (軍配者たちは4体の絵を着る・src/render.ts drawHollowActor)。
const BORROWS: Record<string, readonly string[]> = {
  'hollow-actor': ['dream-hound', 'bride', 'vigil-knight', 'blade-dancer'],
}

const extraOwners = new Map<string, string>()
/** 言葉で見分けられない物 (部屋の背景など) の持ち主を教える。 */
export function setAssetOwners(map: Record<string, string>): void {
  for (const [url, bossId] of Object.entries(map)) extraOwners.set(stripUrl(url), bossId)
}

export type AssetKind = 'scene' | 'battle' | 'late'
export type AssetTier = 0 | 1 | 2 | 3

function stripUrl(url: string): string {
  return url.replace(/^[a-z]+:\/\/[^/]+/i, '').replace(/[?#].*$/, '')
}

/** 遅らせてよい物か (同じ場所の絵・音・JSON・atlas だけ)。 */
export function isQueueableAsset(url: string): boolean {
  if (/^(data|blob):/i.test(url)) return false
  if (/^[a-z]+:\/\//i.test(url) && typeof location !== 'undefined' && !url.startsWith(location.origin)) return false
  return /^\/(sprites|fx|sfx|bg|adv|ui|flashback)\//.test(stripUrl(url))
}

export function assetOwner(url: string): string | null {
  const path = stripUrl(url)
  const known = extraOwners.get(path)
  if (known) return known
  const lower = path.toLowerCase()
  if (SHARED_WORDS.test(lower)) return null
  // ボスの名前のフォルダにある物はそのボスの物 (緩い言葉より先。 /sprites/bosses/vigil-knight/exec_backcoil.webp を
  //   exec_ で処刑人の物と見て番まで止めると、騎士の絵がそろわず戦いが始まる＝11-03 遅い回線の通しで見つけた)。
  for (const id of ASSET_BOSS_ORDER) if (lower.includes(`/${id}/`)) return id
  for (const [re, id] of OWNER_WORDS) if (re.test(lower)) return id
  return null
}

export function assetKind(url: string): AssetKind {
  const path = stripUrl(url)
  if (/^\/(adv|flashback|ui)\//.test(path) || /^\/bg\/title/.test(path) || /^\/sfx\/adv_/.test(path)) return 'scene'
  if (/^\/sprites\/fx\/finisher\//.test(path)) return 'late'
  return 'battle'
}

export type AssetFocus = { current: string | null; next: string | null; nextIsNear: boolean }

/** いまの段。nextIsNear = 幕間など、次のボスの場面がもうすぐ始まる時。 */
export function assetTier(url: string, focus: AssetFocus): AssetTier {
  const owner = assetOwner(url)
  const kind = assetKind(url)
  const hot = new Set<string>()
  const warm = new Set<string>()
  const add = (set: Set<string>, id: string | null) => { if (id) { set.add(id); for (const b of BORROWS[id] ?? []) set.add(b) } }
  add(hot, focus.current)
  if (focus.nextIsNear) add(hot, focus.next)
  else add(warm, focus.next)
  if (kind === 'late') return owner && !hot.has(owner) ? 3 : 2
  if (owner === null || hot.has(owner)) return kind === 'scene' ? 0 : 1
  if (warm.has(owner)) return kind === 'scene' ? 1 : 2
  return 3
}

// 並べ方: 段 → 同じ段では会話の物を先に → 本編で早いボス → 頼まれた順。
function sortKey(url: string, tier: AssetTier, seq: number): [number, number, number, number] {
  const owner = assetOwner(url)
  const order = owner ? ASSET_BOSS_ORDER.indexOf(owner as typeof ASSET_BOSS_ORDER[number]) : -1
  return [tier, assetKind(url) === 'scene' ? 0 : 1, order, seq]
}

/** 段0 が読んでいる間に段1 を同時に読む本数。 */
export const TIER1_WHILE_SCENE = 2
/** 段1 だけの時に同時に読む本数。 */
export const TIER1_ALONE = 6
/** 段2・段3 を同時に読む本数 (段0・段1 が無い時だけ)。 */
export const TIER_LATER = 2

type Job = { url: string; seq: number; tier: AssetTier; start: (done: () => void) => void; cancelled: boolean }

/** 本編の順で、そのボスの物とみなす持ち主 (借りる衣装を含む)。 */
function ownersOf(bossId: string): Set<string> {
  return new Set([bossId, ...(BORROWS[bossId] ?? [])])
}

/** 頼まれた物を段ごとに並べ、空いた枠に流す。始めた物は done を呼ぶまで数える。 */
export class AssetQueue {
  private waiting: Job[] = []
  private running: Job[] = []
  private seq = 0
  private focus: AssetFocus = { current: null, next: null, nextIsNear: false }
  /** 段3 を番まで止めるか (false = 11-02 の版・段3 も空いたら読む)。 */
  holdFar = true

  /** 頼む。戻り値を呼ぶと、まだ始まっていなければ取り消す。 */
  request(url: string, start: (done: () => void) => void): () => void {
    const job: Job = { url, seq: this.seq++, tier: assetTier(url, this.focus), start, cancelled: false }
    this.waiting.push(job)
    this.pump()
    return () => { job.cancelled = true; this.waiting = this.waiting.filter((j) => j !== job) }
  }

  setFocus(focus: AssetFocus): void {
    if (focus.current === this.focus.current && focus.next === this.focus.next && focus.nextIsNear === this.focus.nextIsNear) return
    this.focus = focus
    for (const job of this.waiting) job.tier = assetTier(job.url, focus)
    this.pump()
  }

  /** そのボスの戦いの物 (会話の絵・致命の FX を除く) で、まだ並んでいるか読んでいる最中の本数。戦いの頭の待ち (main.ts setBattleAssetsReadyCheck) が見る。 */
  pendingFor(bossId: string): number {
    const owners = ownersOf(bossId)
    const mine = (j: Job) => assetKind(j.url) === 'battle' && owners.has(assetOwner(j.url) ?? '')
    return this.waiting.filter(mine).length + this.running.filter(mine).length
  }

  /** 並んでいる物の一覧 (撮影・見張り用)。 */
  snapshot(): { url: string; tier: AssetTier; owner: string | null; running: boolean }[] {
    return [
      ...this.running.map((j) => ({ url: j.url, tier: j.tier, owner: assetOwner(j.url), running: true })),
      ...this.waiting.map((j) => ({ url: j.url, tier: j.tier, owner: assetOwner(j.url), running: false })),
    ]
  }

  /** 並んでいる本数・読んでいる本数 (撮影の記録用)。 */
  stats(): { waiting: number; running: number; waitingByTier: number[] } {
    const waitingByTier = [0, 0, 0, 0]
    for (const j of this.waiting) waitingByTier[j.tier] += 1
    return { waiting: this.waiting.length, running: this.running.length, waitingByTier }
  }

  private pump(): void {
    this.waiting.sort((a, b) => {
      const ka = sortKey(a.url, a.tier, a.seq), kb = sortKey(b.url, b.tier, b.seq)
      for (let i = 0; i < ka.length; i += 1) if (ka[i] !== kb[i]) return ka[i] - kb[i]
      return 0
    })
    while (this.waiting.length) {
      const job = this.waiting[0]
      if (!this.mayStart(job.tier)) break
      this.waiting.shift()
      this.running.push(job)
      let finished = false
      job.start(() => {
        if (finished) return
        finished = true
        this.running.splice(this.running.indexOf(job), 1)
        this.pump()
      })
    }
  }

  private mayStart(tier: AssetTier): boolean {
    if (tier === 0) return true
    if (tier === 3 && this.holdFar) return false
    const sceneBusy = this.running.some((j) => j.tier === 0) || this.waiting.some((j) => j.tier === 0)
    const tier1Plus = this.running.filter((j) => j.tier >= 1).length
    if (tier === 1) return tier1Plus < (sceneBusy ? TIER1_WHILE_SCENE : TIER1_ALONE)
    const earlierBusy = sceneBusy || this.running.some((j) => j.tier === 1) || this.waiting.some((j) => j.tier === 1)
    return !earlierBusy && tier1Plus < TIER_LATER
  }
}
